import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Building2, MapPin, Award, FileText, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import config from '../config';

export const InstallerRegisterForm = () => {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    license_number: '',
    experience_years: 0,
    service_areas: [],
    bio: '',
    phone: user?.phone || '',
  });
  const [cityInput, setCityInput] = useState('');
  const [documents, setDocuments] = useState({
    license_file: null,
    insurance_file: null,
  });
  
  const [step, setStep] = useState(1);
  const steps = ['اطلاعات شرکت', 'مدارک', 'تایید نهایی'];

  const handleAddCity = () => {
    if (cityInput.trim() && !formData.service_areas.includes(cityInput.trim())) {
      setFormData({
        ...formData,
        service_areas: [...formData.service_areas, cityInput.trim()]
      });
      setCityInput('');
    }
  };

  const handleRemoveCity = (city) => {
    setFormData({
      ...formData,
      service_areas: formData.service_areas.filter(c => c !== city)
    });
  };

  const handleFileChange = (e, type) => {
    setDocuments({ ...documents, [type]: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('company_name', formData.company_name);
      submitData.append('license_number', formData.license_number);
      submitData.append('experience_years', formData.experience_years);
      submitData.append('service_areas', JSON.stringify(formData.service_areas));
      submitData.append('bio', formData.bio);
      
      if (documents.license_file) {
        submitData.append('license_document', documents.license_file);
      }
      if (documents.insurance_file) {
        submitData.append('insurance_document', documents.insurance_file);
      }
      
      const response = await fetch(config.getApiUrl('/accounts/register/installer/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });
      
      if (response.ok) {
        toast.success('درخواست ثبت‌نام شما با موفقیت ارسال شد. منتظر تایید ادمین باشید.');
        navigate('/profile');
      } else {
        const error = await response.json();
        toast.error(error.message || 'خطا در ثبت‌نام');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-blue-600 p-6 text-white">
            <h1 className="text-2xl font-bold">ثبت‌نام به عنوان نصاب</h1>
            <p className="text-blue-100 mt-2">لطفاً اطلاعات شرکت خود را کامل کنید</p>
          </div>
          
          {/* Steps */}
          <div className="flex border-b">
            {steps.map((stepName, idx) => (
              <div
                key={idx}
                className={`flex-1 py-3 text-center font-medium ${
                  step > idx ? 'text-green-600 border-b-2 border-green-600' :
                  step === idx + 1 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'
                }`}
              >
                <span className="inline-block w-6 h-6 rounded-full mr-2 text-sm text-center leading-5">
                  {step > idx ? '✓' : idx + 1}
                </span>
                {stepName}
              </div>
            ))}
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-1">نام شرکت *</label>
                  <div className="relative">
                    <Building2 className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      className="w-full border rounded-lg pr-10 py-2"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">شماره پروانه/مجوز *</label>
                  <div className="relative">
                    <Award className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.license_number}
                      onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                      className="w-full border rounded-lg pr-10 py-2"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">سال‌های تجربه *</label>
                  <input
                    type="number"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({...formData, experience_years: parseInt(e.target.value)})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">شماره تماس</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                  />
                  <p className="text-xs text-gray-400 mt-1">شماره تماس ثبت‌نام شده شما</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">محدوده فعالیت (شهرها) *</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={cityInput}
                      onChange={(e) => setCityInput(e.target.value)}
                      className="flex-1 border rounded-lg px-3 py-2"
                      placeholder="نام شهر را وارد کنید"
                    />
                    <button
                      type="button"
                      onClick={handleAddCity}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                      افزودن
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.service_areas.map((city) => (
                      <span key={city} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        {city}
                        <button type="button" onClick={() => handleRemoveCity(city)} className="text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">معرفی شرکت</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    rows={4}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="درباره شرکت، سوابق و تخصص‌های خود بنویسید..."
                  />
                </div>
              </div>
            )}
            
            {step === 2 && (
              <div className="space-y-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="font-medium">بارگذاری مدارک</p>
                  <p className="text-sm text-gray-500 mt-1">پروانه کسب، بیمه و سایر مدارک مرتبط</p>
                  
                  <div className="mt-4 space-y-3">
                    <div className="bg-gray-50 p-3 rounded-lg text-right">
                      <label className="cursor-pointer flex justify-between items-center">
                        <span>مدرک پروانه کسب</span>
                        <span className="text-blue-600">
                          {documents.license_file ? documents.license_file.name : 'انتخاب فایل'}
                        </span>
                        <input type="file" className="hidden" onChange={(e) => handleFileChange(e, 'license_file')} />
                      </label>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg text-right">
                      <label className="cursor-pointer flex justify-between items-center">
                        <span>مدرک بیمه</span>
                        <span className="text-blue-600">
                          {documents.insurance_file ? documents.insurance_file.name : 'انتخاب فایل'}
                        </span>
                        <input type="file" className="hidden" onChange={(e) => handleFileChange(e, 'insurance_file')} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {step === 3 && (
              <div className="space-y-6 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold">اطلاعات با موفقیت ثبت شد</h3>
                <p className="text-gray-600">
                  اطلاعات شما برای بررسی ارسال شد. پس از تایید ادمین، می‌توانید به عنوان نصاب فعالیت کنید.
                </p>
              </div>
            )}
            
            <div className="flex justify-between mt-8 pt-4 border-t">
              {step > 1 && (
                <button type="button" onClick={() => setStep(step - 1)} className="px-6 py-2 border rounded-lg">
                  قبلی
                </button>
              )}
              {step < 3 && (
                <button type="button" onClick={() => setStep(step + 1)} className="px-6 py-2 bg-blue-600 text-white rounded-lg ml-auto">
                  بعدی
                </button>
              )}
              {step === 3 && (
                <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 ml-auto">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'ثبت نهایی'}
                  ثبت نهایی
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
